import type { WorkspaceState, Conversation, ConversationTurn } from "./workspace-types"
import type { SocketEnvelope } from "./socket-protocol"

export type ReducerEffect =
  | { type: "flushPendingCancel"; conversationId: string }
  | { type: "resendCancelAfterAttach"; conversationId: string; serverTurnId: string }
  | { type: "turnDelivered"; serverTurnId: string }
  | { type: "consentRequired"; consentVersion: string }
  | { type: "sessionExpired" }

// Transport bookkeeping is derived from the optimistic/live turns. This function
// never accesses storage, a clock, a socket, or application globals.
export function reduceSocketEvent(input: WorkspaceState, envelope: SocketEnvelope): { state: WorkspaceState; effects: ReducerEffect[] } {
  if (envelope.event_id && input.socketEventIds?.includes(envelope.event_id)) return { state: input, effects: [] }
  let state = input
  const effects: ReducerEffect[] = []
  const MAX_RETRY_ATTEMPTS = 2
  const nowLabel = () => envelope.ts
  const getWorkspaceState = () => state
  const setState = (next: WorkspaceState) => { state = next }
  const normalizeLivePhase = (phase: ConversationTurn["livePhase"]) => phase === "streaming" || phase === "settling" || phase === "thinking" ? phase : undefined
  const isCancelableLiveTurn = (turn: ConversationTurn) => (turn.status === "thinking" || turn.status === "streaming") && turn.livePhase !== "settling"
  const pendingSocketSubmits = new Map(input.conversations.flatMap(c => c.turns.filter(t => t.status === "thinking" || t.status === "streaming").map(t => [t.id, { conversationId: c.id, localTurnId: t.id, serverTurnId: t.serverTurnId }] as const)))
function getTurnRetryCount(turn: ConversationTurn) {
  return typeof turn.retryCount === "number" && Number.isFinite(turn.retryCount)
    ? turn.retryCount
    : 0
}

function buildFailedTurnState(turn: ConversationTurn, payload: Record<string, unknown>) {
  const retryable = typeof payload.retryable === "boolean" ? payload.retryable : false
  const retryCount = typeof payload.retry_count === "number" ? payload.retry_count : getTurnRetryCount(turn)
  const permanentFailure = retryable && retryCount >= MAX_RETRY_ATTEMPTS
  const details =
    payload.details && typeof payload.details === "object"
      ? (payload.details as Record<string, unknown>)
      : null
  const billing =
    payload.billing && typeof payload.billing === "object"
      ? (payload.billing as Record<string, unknown>)
      : null
  const availableBalancePaise =
    billing && typeof billing.available_balance_paise === "number"
      ? billing.available_balance_paise
      : details && typeof details.available_balance_paise === "number"
        ? details.available_balance_paise
        : turn.availableBalancePaise ?? null
  const amountDebitedPaise =
    billing && typeof billing.amount_debited_paise === "number"
      ? billing.amount_debited_paise
      : turn.amountDebitedPaise ?? null

  return {
    ...turn,
    status: "failed" as const,
    livePhase: undefined,
    errorCode: permanentFailure
      ? "permanent_failure"
      : typeof payload.code === "string"
        ? payload.code
        : "turn_failed",
    errorMessage: permanentFailure
      ? "This question has failed repeatedly. Please report it."
      : typeof payload.message === "string"
        ? payload.message
        : "The answer could not be completed.",
    errorDetails: details ?? undefined,
    retryable: permanentFailure ? false : retryable,
    retryCount,
    cancelRequested: false,
    availableBalancePaise,
    amountDebitedPaise,
    progressMessages: [],
    followUpState: undefined,
    followUpTurnId: undefined,
    followUpConversationId: undefined,
    followUpActionState: undefined,
  }
}

function annotateFailedTurnFollowUps(
  turns: ConversationTurn[],
  nextTurnId: string,
  options?: { retryOfTurnId?: string; sourceFailedTurnId?: string; sourceFailedConversationId?: string }
) {
  const matchesTurnReference = (turn: ConversationTurn, referenceId: string | undefined) =>
    Boolean(referenceId) && (turn.id === referenceId || turn.serverTurnId === referenceId)

  return turns.map((existingTurn) => {
    if (existingTurn.status !== "failed" || existingTurn.followUpState) {
      return existingTurn
    }
    if (matchesTurnReference(existingTurn, options?.retryOfTurnId)) {
      return {
        ...existingTurn,
        followUpState: "retried" as const,
        followUpTurnId: nextTurnId,
        followUpActionState: undefined,
      }
    }
    if (matchesTurnReference(existingTurn, options?.sourceFailedTurnId)) {
      return {
        ...existingTurn,
        followUpState: "edited" as const,
        followUpTurnId: nextTurnId,
        followUpActionState: undefined,
      }
    }
    if (
      options?.sourceFailedConversationId &&
      matchesTurnReference(existingTurn, options.sourceFailedTurnId)
    ) {
      return {
        ...existingTurn,
        followUpState: "new_chat" as const,
        followUpConversationId: options.sourceFailedConversationId,
        followUpActionState: undefined,
      }
    }
    return {
      ...existingTurn,
      followUpState: "continued" as const,
      followUpTurnId: nextTurnId,
      followUpActionState: undefined,
    }
  })
}

function applySuccessfulTurnFollowUp(conversationId: string, localTurnId: string) {
  const current = getWorkspaceState()
  const conversation = current.conversations.find((entry) => entry.id === conversationId)
  const successfulTurn = conversation?.turns.find((entry) => entry.id === localTurnId)
  if (!conversation || !successfulTurn) {
    return
  }

  const nextTurnId = successfulTurn.serverTurnId ?? successfulTurn.id
  const nextTurns = annotateFailedTurnFollowUps(conversation.turns, nextTurnId, {
    retryOfTurnId: successfulTurn.retryOfTurnId,
    sourceFailedTurnId: successfulTurn.sourceFailedTurnId,
  })
  const changed = nextTurns.some((turn, index) => turn !== conversation.turns[index])
  if (!changed) {
    return
  }

  setState({
    ...current,
    conversations: current.conversations.map((entry) =>
      entry.id === conversationId
        ? {
            ...entry,
            turns: nextTurns,
            updatedAt: nowLabel(),
          }
        : entry
    ),
  })
}

function findPendingSubmitByConversationId(conversationId: string) {
  for (const submit of pendingSocketSubmits.values()) {
    if (submit.conversationId === conversationId) {
      return submit
    }
  }
  return null
}

function updateConversationTurn(
  conversationId: string,
  matcher: (turn: ConversationTurn) => boolean,
  updater: (turn: ConversationTurn) => ConversationTurn,
  conversationPatch?: Partial<Conversation>,
  rootPatch?: Partial<WorkspaceState>
) {
  const current = getWorkspaceState()
  let updated = false
  const conversations = current.conversations.map((conversation) => {
    if (conversation.id !== conversationId) {
      return conversation
    }
    const index = conversation.turns.findIndex(matcher)
    if (index === -1) {
      return conversation
    }
    updated = true
    const turns = conversation.turns.map((turn, turnIndex) =>
      turnIndex === index ? updater(turn) : turn
    )
    return {
      ...conversation,
      ...conversationPatch,
      turns,
      updatedAt: conversationPatch?.updatedAt ?? nowLabel(),
    }
  })
  if (!updated) {
    return false
  }
  setState({
    ...current,
    ...rootPatch,
    conversations,
    activeConversationId: conversationId,
  })
  return true
}

function matchesLiveTurn(
  turn: ConversationTurn,
  serverTurnId?: string,
) {
  if (serverTurnId) {
    return (
      turn.serverTurnId === serverTurnId ||
      ((turn.status === "thinking" || turn.status === "streaming") &&
        !turn.serverTurnId)
    )
  }
  return (
    (turn.status === "thinking" || turn.status === "streaming") &&
    !turn.serverTurnId
  )
}

function applySocketTurnAccepted(
  conversationId: string,
  localTurnId: string,
  payload: Record<string, unknown>
) {
  const serverTurnId = typeof payload.turn_id === "string" ? payload.turn_id : undefined
  const requestMessageId = typeof payload.message_id === "string" ? payload.message_id : undefined
  updateConversationTurn(
    conversationId,
    (turn) => turn.id === localTurnId || matchesLiveTurn(turn, serverTurnId),
    (turn) => ({
      ...turn,
      serverTurnId,
      requestMessageId,
      progressMessages:
        turn.progressMessages.length > 0
          ? turn.progressMessages
          : ["Question Accepted"],
    })
  )
}

function applySocketTurnAttached(
  conversationId: string,
  localTurnId: string,
  payload: Record<string, unknown>
) {
  const serverTurnId = typeof payload.turn_id === "string" ? payload.turn_id : undefined
  const cancelRequested = typeof payload.cancel_requested === "boolean" ? payload.cancel_requested : false
  const resume =
    payload.resume && typeof payload.resume === "object"
      ? (payload.resume as Record<string, unknown>)
      : null
  const resumePhase = normalizeLivePhase(
    typeof resume?.phase === "string" ? (resume.phase as ConversationTurn["livePhase"]) : undefined
  )
  const progressMessages = Array.isArray(resume?.progress_messages)
    ? resume.progress_messages.filter((message): message is string => typeof message === "string")
    : []
  const nextLivePhase =
    resumePhase === "streaming" || resumePhase === "settling"
      ? resumePhase
      : "thinking"
  const answerSnapshot =
    typeof resume?.plain_answer === "string" ? resume.plain_answer : ""
  const detailedAnswerSnapshot =
    typeof resume?.detailed_answer === "string" ? resume.detailed_answer : ""
  updateConversationTurn(
    conversationId,
    (turn) => (turn.status === "thinking" || turn.status === "streaming") && (turn.id === localTurnId || matchesLiveTurn(turn, serverTurnId)),
    (turn) => {
      const isVisibleStreaming = nextLivePhase === "streaming" || nextLivePhase === "settling"
      return {
        ...turn,
        serverTurnId,
        cancelRequested: nextLivePhase === "settling" ? false : Boolean(turn.cancelRequested || cancelRequested),
        livePhase: nextLivePhase,
        status: isVisibleStreaming ? "streaming" : "thinking",
        answer: turn.cancelRequested && nextLivePhase !== "settling" ? turn.answer : isVisibleStreaming ? answerSnapshot : "",
        detailedAnswer: turn.cancelRequested && nextLivePhase !== "settling" ? turn.detailedAnswer : isVisibleStreaming ? detailedAnswerSnapshot : "",
        progressMessages: isVisibleStreaming ? [] : progressMessages,
      }
    }
  )
}

function isLocalTurnCancellationRequested(conversationId: string, localTurnId: string) {
  const conversation = getWorkspaceState().conversations.find((entry) => entry.id === conversationId)
  const turn = conversation?.turns.find((entry) => entry.id === localTurnId)
  return Boolean(turn?.cancelRequested)
}

function resendTurnCancelAfterAttach(
  conversationId: string,
  localTurnId: string,
  payload: Record<string, unknown>
) {
  if (payload.status === "completed" || payload.status === "failed" || payload.status === "settling") {
    return
  }
  const turn = getWorkspaceState().conversations.find((entry) => entry.id === conversationId)
    ?.turns.find((entry) => entry.id === localTurnId)
  if (turn && isCancelableLiveTurn(turn) && turn.cancelRequested) {
    effects.push({ type: "resendCancelAfterAttach", conversationId, serverTurnId: turn.serverTurnId! })
  }
}

function appendTurnProgress(
  conversationId: string,
  message: string,
  serverTurnId?: string
) {
  const normalized = message.trim()
  if (!normalized) {
    return
  }
  updateConversationTurn(
    conversationId,
    (turn) =>
      turn.status === "thinking" && !turn.cancelRequested &&
      (turn.serverTurnId === serverTurnId ||
        (!serverTurnId && turn.serverTurnId == null)),
    (turn) => ({
      ...turn,
      progressMessages:
        turn.progressMessages.at(-1) === normalized
          ? turn.progressMessages
          : [...turn.progressMessages, normalized],
    })
  )
}

function appendTurnProgressEntries(
  conversationId: string,
  messages: string[],
  serverTurnId?: string
) {
  for (const message of messages) {
    appendTurnProgress(conversationId, message, serverTurnId)
  }
}

function markTurnCancellationRequested(
  conversationId: string,
  serverTurnId?: string
) {
  updateConversationTurn(
    conversationId,
    (turn) => isCancelableLiveTurn(turn) && matchesLiveTurn(turn, serverTurnId),
    (turn) => {
      const nextProgressMessage = "Stopping…"
      return {
        ...turn,
        cancelRequested: true,
        progressMessages:
          turn.progressMessages.at(-1) === nextProgressMessage
            ? turn.progressMessages
            : [...turn.progressMessages, nextProgressMessage],
      }
    }
  )
}

function humanizeToolLabel(raw: string): string {
  const cleaned = raw.replace(/^tool::/, "").trim()
  if (!cleaned) return ""
  // If it already looks human (has spaces or lowercase sentence), keep as-is.
  if (/\s/.test(cleaned)) return cleaned
  return cleaned
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .toLowerCase()
    .replace(/^./, (c) => c.toUpperCase())
}

function buildThinkingLabel(
  conversationId: string,
  serverTurnId: string | undefined,
  cycle: number
): string {
  const state = getWorkspaceState()
  const conversation = state.conversations.find((c) => c.id === conversationId)
  const turn = conversation?.turns.find((t) =>
    serverTurnId
      ? t.serverTurnId === serverTurnId
      : t.status === "thinking" || t.status === "streaming"
  )
  const messages = turn?.progressMessages ?? []

  const trailingTools: string[] = []
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const m = messages[i]
    if (m.startsWith("tool::")) {
      trailingTools.unshift(humanizeToolLabel(m))
    } else {
      break
    }
  }

  if (trailingTools.length === 0) {
    return cycle <= 1 ? "Reading natal charts" : "Thinking further"
  }

  if (trailingTools.length === 1) {
    return `Reviewing ${trailingTools[0]}`
  }
  return `Reviewing ${trailingTools.length} findings`
}

function completeSocketTurn(payload: Record<string, unknown>) {
  const conversationId =
    typeof payload.conversation_id === "string" ? payload.conversation_id : ""
  const serverTurnId = typeof payload.turn_id === "string" ? payload.turn_id : undefined
  const answerMessageId = typeof payload.message_id === "string" ? payload.message_id : undefined
  const answer =
    typeof payload.plain_answer === "string"
      ? payload.plain_answer
      : typeof payload.content === "string"
        ? payload.content
        : ""
  const detailedAnswer =
    typeof payload.detailed_answer === "string" ? payload.detailed_answer : ""
  const suggestions = Array.isArray(payload.follow_up_suggestions)
    ? payload.follow_up_suggestions.filter(
        (item): item is string => typeof item === "string"
      )
    : []
  const billing =
    payload.billing && typeof payload.billing === "object"
      ? (payload.billing as Record<string, unknown>)
      : null
  const nextCredits =
    billing && typeof billing.available_balance_paise === "number"
      ? billing.available_balance_paise
      : undefined

  const pendingSubmit =
    (serverTurnId
      ? [...pendingSocketSubmits.values()].find(
          (submit) => submit.serverTurnId === serverTurnId
        )
      : undefined) ?? findPendingSubmitByConversationId(conversationId)
  if (pendingSubmit) {
    pendingSocketSubmits.delete(pendingSubmit.localTurnId)
  }

  updateConversationTurn(
    conversationId,
    (turn) =>
      turn.serverTurnId === serverTurnId ||
      turn.id === pendingSubmit?.localTurnId ||
      ((turn.status === "thinking" || turn.status === "streaming") &&
        (serverTurnId ? !turn.serverTurnId || turn.serverTurnId === serverTurnId : !turn.serverTurnId)),
    (turn) => ({
      ...turn,
      answer,
      detailedAnswer,
      suggestions,
      status: "complete",
      livePhase: undefined,
      serverTurnId,
      answerMessageId: answerMessageId ?? turn.answerMessageId,
      amountDebitedPaise:
        billing && typeof billing.amount_debited_paise === "number"
          ? billing.amount_debited_paise
          : turn.amountDebitedPaise ?? null,
      availableBalancePaise:
        billing && typeof billing.available_balance_paise === "number"
          ? billing.available_balance_paise
          : turn.availableBalancePaise ?? null,
      cancelRequested: false,
      progressMessages: [],
      errorCode: undefined,
      errorMessage: undefined,
      errorDetails: undefined,
      retryable: undefined,
      retryCount: typeof payload.retry_count === "number" ? payload.retry_count : turn.retryCount,
    }),
    undefined,
    nextCredits !== undefined ? { credits: nextCredits } : undefined
  )
  if (pendingSubmit) {
    applySuccessfulTurnFollowUp(conversationId, pendingSubmit.localTurnId)
  }
}

function failSocketTurn(payload: Record<string, unknown>) {
  const serverTurnId = typeof payload.turn_id === "string" ? payload.turn_id : undefined
  let conversationId = typeof payload.conversation_id === "string" ? payload.conversation_id : ""
  let localTurnId: string | undefined

  if (serverTurnId) {
    for (const submit of pendingSocketSubmits.values()) {
      if (submit.serverTurnId === serverTurnId) {
        conversationId = submit.conversationId
        localTurnId = submit.localTurnId
        break
      }
    }
  }
  if (!conversationId) {
    const fallback = pendingSocketSubmits.size === 1 ? [...pendingSocketSubmits.values()][0] : undefined
    if (fallback) {
      conversationId = fallback.conversationId
      localTurnId = fallback.localTurnId
    }
  }
  if (!conversationId && serverTurnId) {
    conversationId = getWorkspaceState().conversations.find(c => c.turns.some(t => t.serverTurnId === serverTurnId))?.id ?? ""
  }
  if (!conversationId) return
  if (localTurnId) {
    pendingSocketSubmits.delete(localTurnId)
  }
  const billing =
    payload.billing && typeof payload.billing === "object"
      ? (payload.billing as Record<string, unknown>)
      : null
  const nextCredits =
    billing && typeof billing.available_balance_paise === "number"
      ? billing.available_balance_paise
      : undefined
  updateConversationTurn(
    conversationId,
    (turn) =>
      turn.serverTurnId === serverTurnId ||
      turn.id === localTurnId ||
      ((turn.status === "thinking" || turn.status === "streaming") &&
        (serverTurnId ? !turn.serverTurnId || turn.serverTurnId === serverTurnId : true)),
    (turn) => ({
      ...buildFailedTurnState(turn, payload),
      serverTurnId,
    }),
    undefined,
    nextCredits !== undefined ? { credits: nextCredits } : undefined
  )
  if (localTurnId) {
    applySuccessfulTurnFollowUp(conversationId, localTurnId)
  }
}

function handleWorkspaceSocketMessage(envelope: SocketEnvelope) {
  const payload =
    envelope.payload && typeof envelope.payload === "object" ? envelope.payload : {}
  switch (envelope.event) {
    case "turn.accepted": {
      const conversationId =
        typeof payload.conversation_id === "string" ? payload.conversation_id : ""
      if (!conversationId) {
        return
      }
      const pendingSubmit = findPendingSubmitByConversationId(conversationId)
      if (!pendingSubmit) {
        return
      }
      const serverTurnId =
        typeof payload.turn_id === "string" ? payload.turn_id : undefined
      const wasCancelRequested = isLocalTurnCancellationRequested(
        conversationId,
        pendingSubmit.localTurnId
      )
      pendingSocketSubmits.set(pendingSubmit.localTurnId, {
        ...pendingSubmit,
        serverTurnId,
      })
      applySocketTurnAccepted(conversationId, pendingSubmit.localTurnId, payload)
      applySuccessfulTurnFollowUp(conversationId, pendingSubmit.localTurnId)
      if (wasCancelRequested) {
        effects.push({ type: "flushPendingCancel", conversationId })
      }
      return
    }
    case "turn.attached": {
      const conversationId =
        typeof payload.conversation_id === "string" ? payload.conversation_id : ""
      const serverTurnId =
        typeof payload.turn_id === "string" ? payload.turn_id : undefined
      if (!conversationId || !serverTurnId) {
        return
      }
      const pendingSubmit = findPendingSubmitByConversationId(conversationId)
      if (pendingSubmit) {
        const wasCancelRequested = isLocalTurnCancellationRequested(
          conversationId,
          pendingSubmit.localTurnId
        )
        pendingSocketSubmits.set(pendingSubmit.localTurnId, {
          ...pendingSubmit,
          serverTurnId,
        })
        applySocketTurnAttached(conversationId, pendingSubmit.localTurnId, payload)
        applySuccessfulTurnFollowUp(conversationId, pendingSubmit.localTurnId)
        if (wasCancelRequested) {
          resendTurnCancelAfterAttach(conversationId, pendingSubmit.localTurnId, payload)
        }
      } else {
        const currentTurn = getWorkspaceState().conversations
          .find((conversation) => conversation.id === conversationId)
          ?.turns.find((turn) => matchesLiveTurn(turn, serverTurnId))
        if (currentTurn) {
          const wasCancelRequested = Boolean(currentTurn.cancelRequested)
          applySocketTurnAttached(conversationId, currentTurn.id, payload)
          applySuccessfulTurnFollowUp(conversationId, currentTurn.id)
          if (wasCancelRequested) {
            resendTurnCancelAfterAttach(conversationId, currentTurn.id, payload)
          }
        }
      }
      return
    }
    case "turn.cancel.requested": {
      const conversationId =
        typeof payload.conversation_id === "string" ? payload.conversation_id : ""
      const serverTurnId =
        typeof payload.turn_id === "string" ? payload.turn_id : undefined
      if (!conversationId) {
        return
      }
      markTurnCancellationRequested(conversationId, serverTurnId)
      return
    }
    case "status.update": {
      const conversationId =
        typeof payload.conversation_id === "string" ? payload.conversation_id : ""
      const content = typeof payload.content === "string" ? payload.content : ""
      const serverTurnId =
        typeof payload.turn_id === "string" ? payload.turn_id : undefined
      if (conversationId && content) {
        appendTurnProgress(conversationId, content, serverTurnId)
      }
      return
    }
    case "llm.thinking": {
      const conversationId =
        typeof payload.conversation_id === "string" ? payload.conversation_id : ""
      const serverTurnId =
        typeof payload.turn_id === "string" ? payload.turn_id : undefined
      const cycle =
        typeof payload.cycle === "number" ? payload.cycle : 0
      if (conversationId) {
        const label = buildThinkingLabel(conversationId, serverTurnId, cycle)
        appendTurnProgress(conversationId, label, serverTurnId)
      }
      return
    }
    case "tool.batch.started": {
      const serverTurnId =
        typeof payload.turn_id === "string" ? payload.turn_id : undefined
      const pendingSubmit =
        (serverTurnId
          ? [...pendingSocketSubmits.values()].find(
              (submit) => submit.serverTurnId === serverTurnId
            )
          : undefined) ?? null
      const tools = Array.isArray(payload.tool_labels)
        ? payload.tool_labels.filter((item): item is string => typeof item === "string")
        : Array.isArray(payload.tools)
          ? payload.tools.filter((item): item is string => typeof item === "string")
          : []
      if (pendingSubmit) {
        const toolEntries =
          tools.length > 0
            ? tools.map((tool) => `tool::${tool}`)
            : ["tool::Chart checks"]
        appendTurnProgressEntries(
          pendingSubmit.conversationId,
          toolEntries,
          serverTurnId
        )
      }
      return
    }
    case "tool.batch.completed": {
      return
    }
    case "conversation.updated": {
      const conversationId =
        typeof payload.conversation_id === "string" ? payload.conversation_id : ""
      const title = typeof payload.title === "string" ? payload.title.trim() : ""
      if (!conversationId || !title) {
        return
      }
      const current = getWorkspaceState()
      setState({
        ...current,
        conversations: current.conversations.map((conversation) =>
          conversation.id === conversationId
            ? {
                ...conversation,
                title,
              }
            : conversation
        ),
      })
      return
    }
    case "conversation.compaction": {
      const conversationId =
        typeof payload.conversation_id === "string" ? payload.conversation_id : ""
      const serverTurnId =
        typeof payload.turn_id === "string" ? payload.turn_id : undefined
      const phase = typeof payload.phase === "string" ? payload.phase : ""
      const message =
        typeof payload.message === "string" && payload.message.trim().length > 0
          ? payload.message
          : phase === "started"
            ? "Compacting earlier conversation to fit the model context"
            : phase === "completed"
              ? "Earlier conversation compacted"
              : phase === "failed"
                ? "Compaction failed and the conversation still exceeds the model context."
                : ""
      if (!conversationId || !message || phase === "skipped") {
        return
      }
      appendTurnProgress(conversationId, message, serverTurnId)
      return
    }
    case "answer.started": {
      const conversationId =
        typeof payload.conversation_id === "string" ? payload.conversation_id : ""
      const serverTurnId = typeof payload.turn_id === "string" ? payload.turn_id : undefined
      if (!conversationId) {
        return
      }
      updateConversationTurn(
        conversationId,
        (turn) => isCancelableLiveTurn(turn) && !turn.cancelRequested && matchesLiveTurn(turn, serverTurnId),
        (turn) => ({
          ...turn,
          status: "streaming",
          livePhase: "streaming",
          progressMessages: [],
        })
      )
      return
    }
    case "answer.delta": {
      const conversationId =
        typeof payload.conversation_id === "string" ? payload.conversation_id : ""
      const serverTurnId = typeof payload.turn_id === "string" ? payload.turn_id : undefined
      const field = typeof payload.field === "string" ? payload.field : ""
      const content = typeof payload.content === "string" ? payload.content : ""
      if (!conversationId || !field || !content) {
        return
      }
      updateConversationTurn(
        conversationId,
        (turn) => isCancelableLiveTurn(turn) && !turn.cancelRequested && matchesLiveTurn(turn, serverTurnId),
        (turn) => ({
          ...turn,
          status: "streaming",
          livePhase: "streaming",
          answer:
            field === "plain_answer" ? `${turn.answer}${content}` : turn.answer,
          detailedAnswer:
            field === "detailed_answer"
              ? `${turn.detailedAnswer}${content}`
              : turn.detailedAnswer,
          progressMessages: [],
        })
      )
      return
    }
    case "answer.final":
      completeSocketTurn(payload)
      if (typeof payload.turn_id === "string") effects.push({ type: "turnDelivered", serverTurnId: payload.turn_id })
      return
    case "turn.settling": {
      const conversationId =
        typeof payload.conversation_id === "string" ? payload.conversation_id : ""
      const serverTurnId = typeof payload.turn_id === "string" ? payload.turn_id : undefined
      if (!conversationId) {
        return
      }
      updateConversationTurn(
        conversationId,
        (turn) => matchesLiveTurn(turn, serverTurnId) && turn.livePhase !== "settling",
        (turn) => ({
          ...turn,
          status: "streaming",
          livePhase: "settling",
          cancelRequested: false,
          progressMessages: [],
        })
      )
      return
    }
    case "turn.error": {
      if (payload.code === "consent_required") {
        const details = payload.details as Record<string, unknown> | undefined
        effects.push({ type: "consentRequired", consentVersion: String(details?.consent_version ?? payload.consent_version ?? "") })
        // BTR admission refusals have no chat identity; only open the disclosure.
        if (!payload.conversation_id && !payload.turn_id) return
      }
      if (payload.code === "session_expired") effects.push({ type: "sessionExpired" })
      failSocketTurn(payload)
      return
    }
    default:
      return
  }
}


  handleWorkspaceSocketMessage(envelope)
  if (state !== input && envelope.event_id) state = { ...state, socketEventIds: [...(input.socketEventIds ?? []).slice(-2047), envelope.event_id] }
  return { state, effects }
}
